import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { ImpersonationTarget } from '../types/models';
import { ApiResponseImpersonationTargets } from '../types/api/ApiResponseImpersonationTargets';

interface UseImpersonationTargetsReturn {
  targets: ImpersonationTarget[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches the list of clients the authenticated user is allowed to impersonate.
 *
 * The `/impersonation/available-targets` endpoint is exempt from the
 * X-Impersonate-Client-ID header (enforced inside apiFetch), so the result
 * always reflects the REAL user's permissions — even if impersonation is
 * currently active.
 */
export const useImpersonationTargets = (): UseImpersonationTargetsReturn => {
  const [targets, setTargets] = useState<ImpersonationTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchTargets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/impersonation/available-targets');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseImpersonationTargets;
      if (data.status === 'success' && data.data?.targets) {
        setTargets(data.data.targets);
      } else {
        setTargets([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load impersonation targets'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchTargets();
    }
  }, [auth.isAuthenticated, fetchTargets]);

  return { targets, loading, error, refetch: fetchTargets };
};

export default useImpersonationTargets;
